import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

// ── Shared modal styles ───────────────────────────────────────────────────────
const modalStyles = {
    dupBox: { backgroundColor: '#1e1a0e', border: '1px solid #f0a500', borderRadius: '4px', padding: '12px 14px', color: '#f0c060', marginBottom: '14px', fontSize: '13px', lineHeight: '1.5' },
    dupLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    backdrop: {
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    },
    modal: {
        backgroundColor: '#1e1e1e', border: '1px solid #444', borderRadius: '10px',
        padding: '28px 32px', width: '580px', maxWidth: '95vw',
        maxHeight: '90vh', overflowY: 'auto',
    },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
    title: { fontSize: '18px', fontWeight: 'bold', color: '#e0e0e0' },
    closeBtn: { background: 'none', border: 'none', color: '#777', fontSize: '18px', cursor: 'pointer', lineHeight: 1 },
    field: { marginBottom: '16px' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
    label: { display: 'block', fontSize: '12px', color: '#aaa', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '9px 12px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    error: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '10px 14px', color: '#cf6679', marginBottom: '16px', fontSize: '13px' },
    enrichBanner: { border: '1px solid', borderRadius: '4px', padding: '9px 13px', marginBottom: '16px', fontSize: '13px' },
    lookupBtn: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '9px 14px', fontSize: '13px', cursor: 'pointer' },
    actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' },
    cancelBtn: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px', padding: '9px 18px', fontSize: '14px', cursor: 'pointer' },
    saveBtn: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '9px 20px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    addLinkBtn: { backgroundColor: 'transparent', color: '#bb86fc', border: '1px dashed #555', borderRadius: '4px', padding: '6px 12px', fontSize: '13px', cursor: 'pointer' },
    removeLinkBtn: { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '4px', padding: '7px 9px', fontSize: '12px', cursor: 'pointer', flexShrink: 0 },
};

// ── MusicBrainz / TheAudioDB helpers ─────────────────────────────────────────
function mapMBTypeToSharc(mbType, picklists) {
    if (!mbType || !picklists) return '';
    const t = mbType.toLowerCase();
    const match = picklists.artist_types.find(at => {
        const v = at.value.toLowerCase();
        if (t === 'person') return v.includes('solo') || v === 'person';
        if (t === 'group') return v.includes('band') || v.includes('group');
        if (t === 'orchestra') return v.includes('orchestra') || v.includes('ensemble');
        if (t === 'choir') return v.includes('choir') || v.includes('chorus');
        if (t === 'dj') return v.includes('dj');
        return false;
    });
    return match ? String(match.id) : '';
}
function mapGenreToSharc(genre, picklists) {
    if (!genre || !picklists) return '';
    const g = genre.toLowerCase();
    const match = picklists.artist_styles.find(s => {
        const v = s.value.toLowerCase();
        return g.includes(v) || v.includes(g.split(' ')[0]);
    });
    return match ? String(match.id) : '';
}
function mapCountryCodeToId(code, picklists) {
    if (!code || !picklists) return '';
    const match = picklists.countries.find(c =>
        c.code && c.code.toUpperCase() === code.toUpperCase()
    );
    return match ? String(match.id) : '';
}

// ── Quick Create Artist Modal ─────────────────────────────────────────────────
function QuickCreateArtistModal({ picklists, initialName, onCreated, onClose }) {
    const [form, setForm] = useState({
        name: initialName || '',
        city: '',
        country_id: '',
        state_id: '',
        artist_type_id: '',
        artist_style_id: '',
        description: '',
        image_url: '',
    });
    const [links, setLinks] = useState([]);
    const [saving, setSaving] = useState(false);
    const [enriching, setEnriching] = useState(false);
    const [enrichStatus, setEnrichStatus] = useState(null);
    const [enrichNote, setEnrichNote] = useState('');
    const [error, setError] = useState(null);
    const [dupWarning, setDupWarning] = useState(null);

    const filteredStates = picklists
        ? picklists.states_regions.filter(s => s.country_id === parseInt(form.country_id))
        : [];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value, ...(name === 'country_id' ? { state_id: '' } : {}) }));
    };

    const handleEnrich = async () => {
        const name = form.name.trim();
        if (!name) { setError('Enter an artist name first to look up info.'); return; }
        setEnriching(true);
        setEnrichStatus(null);
        setEnrichNote('');
        setError(null);
        let found = { tadb: null, mb: null };
        try {
            const tadbRes = await fetch(`https://www.theaudiodb.com/api/v1/json/2/search.php?s=${encodeURIComponent(name)}`);
            const tadbData = await tadbRes.json();
            if (tadbData.artists && tadbData.artists.length > 0) found.tadb = tadbData.artists[0];
        } catch (e) {}
        try {
            const mbRes = await fetch(
                `https://musicbrainz.org/ws/2/artist/?query=artist:${encodeURIComponent(name)}&fmt=json&limit=3`,
                { headers: { 'User-Agent': 'SHARC/1.0 (sharc-events.com)' } }
            );
            const mbData = await mbRes.json();
            if (mbData.artists && mbData.artists.length > 0) {
                found.mb = mbData.artists.find(a => a.name.toLowerCase() === name.toLowerCase()) || mbData.artists[0];
            }
        } catch (e) {}
        const updates = {};
        const notes = [];
        if (found.tadb) {
            const t = found.tadb;
            if (t.strBiographyEN && !form.description) updates.description = t.strBiographyEN.slice(0, 2000);
            if (t.strArtistThumb && !form.image_url) updates.image_url = t.strArtistThumb;
            if (t.strCountryCode) { const cid = mapCountryCodeToId(t.strCountryCode, picklists); if (cid && !form.country_id) updates.country_id = cid; }
            if (t.strGenre) { const sid = mapGenreToSharc(t.strGenre, picklists); if (sid && !form.artist_style_id) updates.artist_style_id = sid; }
            const newLinks = [];
            if (t.strWebsite) newLinks.push({ description: 'Website', url: t.strWebsite.startsWith('http') ? t.strWebsite : `https://${t.strWebsite}` });
            if (t.strFacebook) newLinks.push({ description: 'Facebook', url: `https://www.facebook.com/${t.strFacebook}` });
            if (t.strTwitter) newLinks.push({ description: 'Twitter', url: `https://www.twitter.com/${t.strTwitter}` });
            if (newLinks.length > 0) setLinks(newLinks);
            notes.push('TheAudioDB');
        }
        if (found.mb) {
            const m = found.mb;
            if (m.type) { const tid = mapMBTypeToSharc(m.type, picklists); if (tid && !updates.artist_type_id && !form.artist_type_id) updates.artist_type_id = tid; }
            if (m.country && !updates.country_id && !form.country_id) { const cid = mapCountryCodeToId(m.country, picklists); if (cid) updates.country_id = cid; }
            if (m.tags && m.tags.length > 0 && !updates.artist_style_id && !form.artist_style_id) {
                const topTag = m.tags.sort((a, b) => b.count - a.count)[0];
                const sid = mapGenreToSharc(topTag.name, picklists);
                if (sid) updates.artist_style_id = sid;
            }
            if (!notes.includes('MusicBrainz')) notes.push('MusicBrainz');
        }
        if (Object.keys(updates).length > 0 || notes.length > 0) {
            setForm(prev => ({ ...prev, ...updates }));
            setEnrichStatus('success');
            setEnrichNote(`Info found via ${notes.join(' + ')}. Review and adjust before saving.`);
        } else {
            setEnrichStatus('none');
            setEnrichNote('No info found for this artist. Fill in manually.');
        }
        setEnriching(false);
    };

    const handleSave = async () => {
        if (!form.name.trim()) { setError('Artist name is required'); return; }
        setSaving(true);
        setError(null);
        setDupWarning(null);
        try {
            const payload = {
                name: form.name.trim(),
                city: form.city.trim() || null,
                country_id: form.country_id ? parseInt(form.country_id) : null,
                state_id: form.state_id ? parseInt(form.state_id) : null,
                artist_type_id: form.artist_type_id ? parseInt(form.artist_type_id) : null,
                artist_style_id: form.artist_style_id ? parseInt(form.artist_style_id) : null,
                description: form.description.trim() || null,
                image_url: form.image_url.trim() || null,
                links: links.filter(l => l.description && l.url),
            };
            const res = await axios.post(`${config.API_URL}/artists`, payload);
            onCreated({ id: res.data.id, name: form.name.trim(), city: form.city.trim() || null });
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.error === 'duplicate') {
                setDupWarning(err.response.data.existing);
            } else {
                setError('Failed to create artist. Please try again.');
            }
            setSaving(false);
        }
    };

    const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };
    const addLink = () => setLinks(prev => [...prev, { description: '', url: '' }]);
    const updateLink = (i, field, val) => setLinks(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
    const removeLink = (i) => setLinks(prev => prev.filter((_, idx) => idx !== i));

    return (
        <div onClick={handleBackdropClick} style={modalStyles.backdrop}>
            <div style={modalStyles.modal}>
                <div style={modalStyles.header}>
                    <span style={modalStyles.title}>Quick Create Artist</span>
                    <button onClick={onClose} style={modalStyles.closeBtn}>✕</button>
                </div>
                {error && <div style={modalStyles.error}>{error}</div>}
                {dupWarning && (
                    <div style={modalStyles.dupBox}>
                        <strong>⚠ Duplicate artist detected.</strong> An artist with this name, city, and location already exists:
                        <div style={{ marginTop: '6px' }}><strong>{dupWarning.name}</strong>{(dupWarning.city || dupWarning.state_name || dupWarning.country_name) && <span> — {[dupWarning.city, dupWarning.state_name, dupWarning.country_name].filter(Boolean).join(', ')}</span>}</div>
                        <div style={{ marginTop: '8px' }}><a href={`/artists/${dupWarning.id}`} target="_blank" rel="noreferrer" style={modalStyles.dupLink}>→ View existing artist</a></div>
                    </div>
                )}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                        <label style={modalStyles.label}>Artist Name *</label>
                        <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. The Midnight" autoFocus style={modalStyles.input} />
                    </div>
                    <button onClick={handleEnrich} disabled={enriching || !form.name.trim()} style={{ ...modalStyles.lookupBtn, opacity: enriching || !form.name.trim() ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                        {enriching ? 'Looking up...' : '🔍 Look Up Info'}
                    </button>
                </div>
                {enrichStatus === 'success' && <div style={{ ...modalStyles.enrichBanner, borderColor: '#4caf50', color: '#4caf50' }}>✓ {enrichNote}</div>}
                {enrichStatus === 'none' && <div style={{ ...modalStyles.enrichBanner, borderColor: '#888', color: '#888' }}>{enrichNote}</div>}
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>City</label>
                    <input name="city" value={form.city} onChange={handleChange} placeholder="e.g. Los Angeles" style={modalStyles.input} />
                </div>
                <div style={modalStyles.twoCol}>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Country</label>
                        <select name="country_id" value={form.country_id} onChange={handleChange} style={modalStyles.input}>
                            <option value="">Select...</option>
                            {picklists && picklists.countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    {form.country_id && filteredStates.length > 0 && (
                        <div style={modalStyles.field}>
                            <label style={modalStyles.label}>State / Region</label>
                            <select name="state_id" value={form.state_id} onChange={handleChange} style={modalStyles.input}>
                                <option value="">Select...</option>
                                {(filteredStates || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                    )}
                </div>
                <div style={modalStyles.twoCol}>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Artist Type</label>
                        <select name="artist_type_id" value={form.artist_type_id} onChange={handleChange} style={modalStyles.input}>
                            <option value="">Select...</option>
                            {picklists && picklists.artist_types.map(t => <option key={t.id} value={t.id}>{t.value}</option>)}
                        </select>
                    </div>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Style / Genre</label>
                        <select name="artist_style_id" value={form.artist_style_id} onChange={handleChange} style={modalStyles.input}>
                            <option value="">Select...</option>
                            {picklists && picklists.artist_styles.map(s => <option key={s.id} value={s.id}>{s.value}</option>)}
                        </select>
                    </div>
                </div>
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>Bio</label>
                    <textarea name="description" value={form.description} onChange={handleChange} rows={4} placeholder="A short description..." style={{ ...modalStyles.input, resize: 'vertical', lineHeight: '1.5', fontFamily: 'inherit' }} />
                </div>
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>Image URL</label>
                    <input name="image_url" value={form.image_url} onChange={handleChange} placeholder="https://..." style={modalStyles.input} />
                    {form.image_url && <img src={form.image_url} alt="Preview" style={{ marginTop: '10px', width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #333' }} onError={e => { e.target.style.display = 'none'; }} />}
                </div>
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>Links</label>
                    {links.map((link, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                            <input value={link.description} onChange={e => updateLink(i, 'description', e.target.value)} placeholder="Label (e.g. Spotify)" style={{ ...modalStyles.input, flex: '0 0 130px' }} />
                            <input value={link.url} onChange={e => updateLink(i, 'url', e.target.value)} placeholder="https://..." style={{ ...modalStyles.input, flex: 1 }} />
                            <button onClick={() => removeLink(i)} style={modalStyles.removeLinkBtn}>✕</button>
                        </div>
                    ))}
                    <button onClick={addLink} style={modalStyles.addLinkBtn}>+ Add Link</button>
                </div>
                <div style={modalStyles.actions}>
                    <button onClick={onClose} style={modalStyles.cancelBtn}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} style={modalStyles.saveBtn}>{saving ? 'Creating...' : 'Create Artist'}</button>
                </div>
            </div>
        </div>
    );
}

// ── Quick Create Festival Modal ───────────────────────────────────────────────
function QuickCreateFestivalModal({ initialName, onCreated, onClose }) {
    const [form, setForm] = useState({ name: initialName || '', start_date: '', end_date: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSave = async () => {
        if (!form.name.trim()) { setError('Festival name is required'); return; }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                name: form.name.trim(),
                start_date: form.start_date || null,
                end_date: form.end_date || null,
                event_ids: [],
                promoter_ids: [],
            };
            const res = await axios.post(`${config.API_URL}/festivals`, payload);
            const fest = res.data.festival || res.data;
            onCreated({ id: fest.id, name: form.name.trim() });
        } catch (err) {
            setError('Failed to create festival. Please try again.');
            setSaving(false);
        }
    };

    const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

    return (
        <div onClick={handleBackdropClick} style={modalStyles.backdrop}>
            <div style={{ ...modalStyles.modal, width: '460px' }}>
                <div style={modalStyles.header}>
                    <span style={modalStyles.title}>Quick Create Festival</span>
                    <button onClick={onClose} style={modalStyles.closeBtn}>✕</button>
                </div>
                {error && <div style={modalStyles.error}>{error}</div>}
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>Festival Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. Coachella, Bonnaroo..." autoFocus style={modalStyles.input} />
                </div>
                <div style={modalStyles.twoCol}>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Start Date</label>
                        <input type="date" name="start_date" value={form.start_date} onChange={handleChange} style={modalStyles.input} />
                    </div>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>End Date</label>
                        <input type="date" name="end_date" value={form.end_date} onChange={handleChange} style={modalStyles.input} />
                    </div>
                </div>
                <p style={{ fontSize: '12px', color: '#777', margin: '0 0 16px 0' }}>You can add events and promoters after creation.</p>
                <div style={modalStyles.actions}>
                    <button onClick={onClose} style={modalStyles.cancelBtn}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} style={modalStyles.saveBtn}>{saving ? 'Creating...' : 'Create Festival'}</button>
                </div>
            </div>
        </div>
    );
}

// ── Quick Create Venue Modal ──────────────────────────────────────────────────
function QuickCreateVenueModal({ picklists, initialName, onCreated, onClose }) {
    const [form, setForm] = useState({
        name: initialName || '',
        street: '',
        city: '',
        country_id: '',
        state_id: '',
        status_id: '',
        venue_type_id: '',
        capacity: '',
        age_limit_id: '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [dupWarning, setDupWarning] = useState(null);

    const countries = (picklists && picklists.countries) || [];
    const venueTypes = (picklists && picklists.venue_types) || [];
    const venueStatuses = (picklists && picklists.venue_statuses) || [];
    const ageLimits = (picklists && picklists.venue_age_limits) || [];
    const filteredStates = (picklists && picklists.states_regions)
        ? picklists.states_regions.filter(s => s.country_id === parseInt(form.country_id))
        : [];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value, ...(name === 'country_id' ? { state_id: '' } : {}) }));
    };

    const handleSave = async () => {
        const required = ['name', 'city', 'country_id', 'state_id', 'status_id', 'venue_type_id', 'capacity', 'age_limit_id'];
        const missing = required.filter(f => !form[f]);
        if (missing.length > 0) { setError('Please fill in all required fields (marked with *).'); return; }
        setSaving(true);
        setError(null);
        setDupWarning(null);
        try {
            const payload = {
                name: form.name.trim(),
                street: form.street.trim() || null,
                city: form.city.trim(),
                country_id: parseInt(form.country_id),
                state_id: parseInt(form.state_id),
                status_id: parseInt(form.status_id),
                venue_type_id: parseInt(form.venue_type_id),
                capacity: parseInt(form.capacity),
                age_limit_id: parseInt(form.age_limit_id),
            };
            const res = await axios.post(`${config.API_URL}/venues`, payload);
            const venue = res.data;
            onCreated({ id: venue.id, name: form.name.trim(), city: form.city.trim() });
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.error === 'duplicate') {
                setDupWarning(err.response.data.existing);
            } else {
                setError('Failed to create venue. Please try again.');
            }
            setSaving(false);
        }
    };

    const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

    return (
        <div onClick={handleBackdropClick} style={modalStyles.backdrop}>
            <div style={modalStyles.modal}>
                <div style={modalStyles.header}>
                    <span style={modalStyles.title}>Quick Create Venue</span>
                    <button onClick={onClose} style={modalStyles.closeBtn}>✕</button>
                </div>
                {error && <div style={modalStyles.error}>{error}</div>}
                {dupWarning && (
                    <div style={modalStyles.dupBox}>
                        <strong>⚠ Duplicate venue detected.</strong> A venue with this name, city, and location already exists:
                        <div style={{ marginTop: '6px' }}><strong>{dupWarning.name}</strong>{(dupWarning.city || dupWarning.state_name || dupWarning.country_name) && <span> — {[dupWarning.city, dupWarning.state_name, dupWarning.country_name].filter(Boolean).join(', ')}</span>}</div>
                        <div style={{ marginTop: '8px' }}><a href={`/venues/${dupWarning.id}`} target="_blank" rel="noreferrer" style={modalStyles.dupLink}>→ View existing venue</a></div>
                    </div>
                )}
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>Venue Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. The Ryman Auditorium" autoFocus style={modalStyles.input} />
                </div>
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>Street Address</label>
                    <input name="street" value={form.street} onChange={handleChange} placeholder="e.g. 116 Fifth Ave N" style={modalStyles.input} />
                </div>
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>City *</label>
                    <input name="city" value={form.city} onChange={handleChange} placeholder="e.g. Nashville" style={modalStyles.input} />
                </div>
                <div style={modalStyles.twoCol}>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Country *</label>
                        <select name="country_id" value={form.country_id} onChange={handleChange} style={modalStyles.input}>
                            <option value="">Select...</option>
                            {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>State / Region *</label>
                        <select name="state_id" value={form.state_id} onChange={handleChange} style={modalStyles.input} disabled={!form.country_id}>
                            <option value="">Select...</option>
                            {filteredStates.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>
                </div>
                <div style={modalStyles.twoCol}>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Venue Type *</label>
                        <select name="venue_type_id" value={form.venue_type_id} onChange={handleChange} style={modalStyles.input}>
                            <option value="">Select...</option>
                            {venueTypes.map(t => <option key={t.id} value={t.id}>{t.value}</option>)}
                        </select>
                    </div>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Status *</label>
                        <select name="status_id" value={form.status_id} onChange={handleChange} style={modalStyles.input}>
                            <option value="">Select...</option>
                            {venueStatuses.map(s => <option key={s.id} value={s.id}>{s.value}</option>)}
                        </select>
                    </div>
                </div>
                <div style={modalStyles.twoCol}>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Capacity *</label>
                        <input type="number" name="capacity" value={form.capacity} onChange={handleChange} placeholder="e.g. 2500" style={modalStyles.input} />
                    </div>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Age Limit *</label>
                        <select name="age_limit_id" value={form.age_limit_id} onChange={handleChange} style={modalStyles.input}>
                            <option value="">Select...</option>
                            {ageLimits.map(a => <option key={a.id} value={a.id}>{a.value}</option>)}
                        </select>
                    </div>
                </div>
                <p style={{ fontSize: '12px', color: '#777', margin: '0 0 16px 0' }}>You can add address, bio, and links after creation.</p>
                <div style={modalStyles.actions}>
                    <button onClick={onClose} style={modalStyles.cancelBtn}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} style={modalStyles.saveBtn}>{saving ? 'Creating...' : 'Create Venue'}</button>
                </div>
            </div>
        </div>
    );
}

// ── Quick Create Promoter Modal ───────────────────────────────────────────────
function QuickCreatePromoterModal({ picklists, initialName, onCreated, onClose }) {
    const [form, setForm] = useState({ name: initialName || '', city: '', country_id: '', state_id: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const filteredStates = picklists
        ? picklists.states_regions.filter(s => s.country_id === parseInt(form.country_id))
        : [];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value, ...(name === 'country_id' ? { state_id: '' } : {}) }));
    };

    const handleSave = async () => {
        if (!form.name.trim()) { setError('Promoter name is required'); return; }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                name: form.name.trim(),
                city: form.city.trim() || null,
                country_id: form.country_id ? parseInt(form.country_id) : null,
                state_id: form.state_id ? parseInt(form.state_id) : null,
            };
            const res = await axios.post(`${config.API_URL}/promoters`, payload);
            onCreated({ id: res.data.id, name: form.name.trim(), city: form.city.trim() || null });
        } catch (err) {
            setError('Failed to create promoter. Please try again.');
            setSaving(false);
        }
    };

    const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

    return (
        <div onClick={handleBackdropClick} style={modalStyles.backdrop}>
            <div style={{ ...modalStyles.modal, width: '460px' }}>
                <div style={modalStyles.header}>
                    <span style={modalStyles.title}>Quick Create Promoter</span>
                    <button onClick={onClose} style={modalStyles.closeBtn}>✕</button>
                </div>
                {error && <div style={modalStyles.error}>{error}</div>}
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>Promoter Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. Live Nation, AEG Presents..." autoFocus style={modalStyles.input} />
                </div>
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>City</label>
                    <input name="city" value={form.city} onChange={handleChange} placeholder="e.g. Los Angeles" style={modalStyles.input} />
                </div>
                <div style={modalStyles.twoCol}>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Country</label>
                        <select name="country_id" value={form.country_id} onChange={handleChange} style={modalStyles.input}>
                            <option value="">Select...</option>
                            {picklists && picklists.countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    {form.country_id && filteredStates.length > 0 && (
                        <div style={modalStyles.field}>
                            <label style={modalStyles.label}>State / Region</label>
                            <select name="state_id" value={form.state_id} onChange={handleChange} style={modalStyles.input}>
                                <option value="">Select...</option>
                                {(filteredStates || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                    )}
                </div>
                <p style={{ fontSize: '12px', color: '#777', margin: '0 0 16px 0' }}>You can add description and links after creation.</p>
                <div style={modalStyles.actions}>
                    <button onClick={onClose} style={modalStyles.cancelBtn}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} style={modalStyles.saveBtn}>{saving ? 'Creating...' : 'Create Promoter'}</button>
                </div>
            </div>
        </div>
    );
}

// ── Searchable Select (supports optional quick-create trigger) ────────────────
function SearchableSelect({ options, value, onChange, placeholder = 'Search...', renderLabel, onCreateItem, createLabel = 'Create new' }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const containerRef = useRef(null);

    const selected = options.find(o => String(o.id) === String(value));
    const inputValue = focused ? query : (selected ? renderLabel(selected) : query);

    const filtered = query.trim()
        ? options.filter(o => renderLabel(o).toLowerCase().includes(query.toLowerCase()))
        : options.slice(0, 50);

    useEffect(() => {
        function handleClickOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false); setFocused(false); setQuery('');
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); };
    const handleFocus = () => { setFocused(true); setQuery(''); setOpen(true); };
    const handleSelect = (option) => { onChange(String(option.id)); setQuery(''); setOpen(false); setFocused(false); };
    const handleClear = (e) => { e.stopPropagation(); onChange(''); setQuery(''); setOpen(false); setFocused(false); };

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder={placeholder}
                    style={{
                        backgroundColor: '#2c2c2c', border: '1px solid #444',
                        borderRadius: open ? '4px 4px 0 0' : '4px',
                        padding: '10px 36px 10px 14px', fontSize: '15px', color: '#e0e0e0',
                        width: '100%', boxSizing: 'border-box', outline: 'none',
                    }}
                    autoComplete="off"
                />
                {value && (
                    <button onClick={handleClear} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#777', cursor: 'pointer', fontSize: '16px', padding: '0' }}>✕</button>
                )}
            </div>
            {open && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, backgroundColor: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: '260px', overflowY: 'auto' }}>
                    {onCreateItem && (
                        <div
                            onMouseDown={(e) => { e.preventDefault(); onCreateItem(query); }}
                            style={{ padding: '9px 14px', fontSize: '13px', color: '#bb86fc', cursor: 'pointer', borderBottom: '1px solid #3a3a3a', display: 'flex', alignItems: 'center', gap: '6px' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2a2040'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <span style={{ fontSize: '15px', lineHeight: 1 }}>&#xFF0B;</span>
                            <span>{createLabel}{query.trim() ? `: "${query.trim()}"` : ''}</span>
                        </div>
                    )}
                    {filtered.length === 0 ? (
                        <div style={{ padding: '10px 14px', color: '#666', fontSize: '14px' }}>No results found</div>
                    ) : (
                        filtered.map(option => (
                            <div
                                key={option.id}
                                onMouseDown={() => handleSelect(option)}
                                style={{ padding: '9px 14px', fontSize: '14px', color: String(option.id) === String(value) ? '#bb86fc' : '#e0e0e0', backgroundColor: String(option.id) === String(value) ? '#1e1e1e' : 'transparent', cursor: 'pointer', borderBottom: '1px solid #333' }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#383838'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = String(option.id) === String(value) ? '#1e1e1e' : 'transparent'}
                            >
                                {renderLabel(option)}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

// ── Promoter Picker (multi-select with quick create) ──────────────────────────
function PromoterPicker({ promoters, selectedIds, onChange, onCreatePromoter }) {
    const [search, setSearch] = useState('');
    const q = search.toLowerCase();
    const filtered = search
        ? promoters.filter(p => [p.name, p.city].some(v => v && v.toLowerCase().includes(q)))
        : promoters;

    const toggle = (id) => {
        if (selectedIds.includes(id)) onChange(selectedIds.filter(i => i !== id));
        else onChange([...selectedIds, id]);
    };

    const selectedPromoters = promoters.filter(p => selectedIds.includes(p.id));
    const showDropdown = search.length > 0;

    return (
        <div>
            {selectedPromoters.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {selectedPromoters.map(p => (
                        <div key={p.id} style={{ backgroundColor: '#2c2c2c', border: '1px solid #bb86fc', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {p.name}
                            <span onClick={() => toggle(p.id)} style={{ cursor: 'pointer', color: '#888' }}>✕</span>
                        </div>
                    ))}
                </div>
            )}
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    placeholder="Search promoters..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: showDropdown ? '4px 4px 0 0' : '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' }}
                    autoComplete="off"
                />
                {showDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: '220px', overflowY: 'auto', zIndex: 100 }}>
                        {onCreatePromoter && (
                            <div
                                onMouseDown={(e) => { e.preventDefault(); onCreatePromoter(search); setSearch(''); }}
                                style={{ padding: '9px 14px', fontSize: '13px', color: '#bb86fc', cursor: 'pointer', borderBottom: '1px solid #3a3a3a', display: 'flex', alignItems: 'center', gap: '6px' }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2a2040'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <span style={{ fontSize: '15px', lineHeight: 1 }}>&#xFF0B;</span>
                                <span>Create new promoter{search.trim() ? `: "${search.trim()}"` : ''}</span>
                            </div>
                        )}
                        {filtered.length === 0 ? (
                            <div style={{ padding: '9px 14px', fontSize: '14px', color: '#666' }}>No results found</div>
                        ) : (
                            filtered.map(p => (
                                <div
                                    key={p.id}
                                    onMouseDown={() => { toggle(p.id); setSearch(''); }}
                                    style={{ padding: '9px 14px', fontSize: '14px', color: selectedIds.includes(p.id) ? '#bb86fc' : '#e0e0e0', cursor: 'pointer', borderBottom: '1px solid #333', backgroundColor: selectedIds.includes(p.id) ? '#1e1e1e' : 'transparent' }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#383838'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = selectedIds.includes(p.id) ? '#1e1e1e' : 'transparent'}
                                >
                                    {p.name}{p.city ? ` — ${p.city}` : ''}
                                    {selectedIds.includes(p.id) && <span style={{ marginLeft: '8px' }}>✓</span>}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── AddEvent ──────────────────────────────────────────────────────────────────
function AddEvent() {
    const navigate = useNavigate();
    const [picklists, setPicklists] = useState(null);
    const [artists, setArtists] = useState([]);
    const [venues, setVenues] = useState([]);
    const [stages, setStages] = useState([]);
    const [festivals, setFestivals] = useState([]);
    const [promoters, setPromoters] = useState([]);
    const [selectedPromoterIds, setSelectedPromoterIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [form, setForm] = useState({
        event_date: '',
        venue_id: '',
        stage_id: '',
        title: '',
        est_attendance_id: '',
        image_url: '',
        festival_id: '',
    });

    const [lineup, setLineup] = useState([
        { artist_id: '', role: 'headliner', lineup_position: 1 }
    ]);

    // Modal state
    const [showCreateArtist, setShowCreateArtist] = useState(false);
    const [createArtistLineupIndex, setCreateArtistLineupIndex] = useState(null);
    const [createArtistInitialName, setCreateArtistInitialName] = useState('');

    const [showCreateFestival, setShowCreateFestival] = useState(false);
    const [createFestivalInitialName, setCreateFestivalInitialName] = useState('');

    const [showCreateVenue, setShowCreateVenue] = useState(false);
    const [createVenueInitialName, setCreateVenueInitialName] = useState('');

    const [showCreatePromoter, setShowCreatePromoter] = useState(false);
    const [createPromoterInitialName, setCreatePromoterInitialName] = useState('');

    useEffect(() => {
        Promise.all([
            axios.get(`${config.API_URL}/picklists`),
            axios.get(`${config.API_URL}/artists`),
            axios.get(`${config.API_URL}/venues`),
            axios.get(`${config.API_URL}/festivals`),
            axios.get(`${config.API_URL}/promoters`),
        ]).then(([picklistsRes, artistsRes, venuesRes, festivalsRes, promotersRes]) => {
            setPicklists(picklistsRes.data);
            setArtists(artistsRes.data);
            setVenues(venuesRes.data);
            setFestivals(festivalsRes.data.festivals || []);
            setPromoters(promotersRes.data.promoters || []);
            setLoading(false);
        }).catch(() => {
            setError('Failed to load form data');
            setLoading(false);
        });
    }, []);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleLineupChange = (index, field, value) => {
        setLineup(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
    };

    const addArtist = () => {
        setLineup(prev => [...prev, { artist_id: '', role: 'support', lineup_position: prev.length + 1 }]);
    };

    const removeArtist = (index) => {
        setLineup(prev => {
            const updated = prev.filter((_, i) => i !== index);
            return updated.map((item, i) => ({ ...item, lineup_position: i + 1 }));
        });
    };

    // Artist quick-create
    const handleOpenCreateArtist = (index, initialName) => {
        setCreateArtistLineupIndex(index);
        setCreateArtistInitialName(initialName || '');
        setShowCreateArtist(true);
    };
    const handleArtistCreated = (newArtist) => {
        setArtists(prev => [...prev, newArtist]);
        if (createArtistLineupIndex !== null) {
            handleLineupChange(createArtistLineupIndex, 'artist_id', String(newArtist.id));
        }
        setShowCreateArtist(false);
    };

    // Festival quick-create
    const handleOpenCreateFestival = (initialName) => {
        setCreateFestivalInitialName(initialName || '');
        setShowCreateFestival(true);
    };
    const handleFestivalCreated = (newFestival) => {
        setFestivals(prev => [...prev, newFestival]);
        setForm(prev => ({ ...prev, festival_id: String(newFestival.id) }));
        setShowCreateFestival(false);
    };

    // Venue quick-create
    const handleOpenCreateVenue = (initialName) => {
        setCreateVenueInitialName(initialName || '');
        setShowCreateVenue(true);
    };
    const handleVenueCreated = (newVenue) => {
        setVenues(prev => [...prev, newVenue]);
        setForm(prev => ({ ...prev, venue_id: String(newVenue.id), stage_id: '' }));
        setStages([]);
        // Fetch the new venue's stages (will auto-create default stage)
        axios.get(`${config.API_URL}/venues/${newVenue.id}`)
            .then(res => {
                const venueStages = res.data.stages || [];
                setStages(venueStages);
                const defaultStage = venueStages.find(s => s.is_default);
                if (defaultStage) setForm(prev => ({ ...prev, stage_id: String(defaultStage.id) }));
            })
            .catch(() => {});
        setShowCreateVenue(false);
    };

    // Promoter quick-create
    const handleOpenCreatePromoter = (initialName) => {
        setCreatePromoterInitialName(initialName || '');
        setShowCreatePromoter(true);
    };
    const handlePromoterCreated = (newPromoter) => {
        setPromoters(prev => [...prev, newPromoter]);
        setSelectedPromoterIds(prev => [...prev, newPromoter.id]);
        setShowCreatePromoter(false);
    };

    const handleSubmit = async () => {
        if (!form.event_date) { setError('Event date is required'); return; }
        if (!form.venue_id) { setError('Venue is required'); return; }
        if (lineup.some(a => !a.artist_id)) { setError('Please select an artist for each lineup slot'); return; }
        const headliners = lineup.filter(a => a.role === 'headliner');
        if (headliners.length !== 1) { setError('Exactly one headliner is required'); return; }

        setSaving(true);
        setError(null);
        try {
            const payload = {
                event_date: form.event_date,
                venue_id: parseInt(form.venue_id),
                stage_id: form.stage_id ? parseInt(form.stage_id) : null,
                title: form.title.trim() || null,
                est_attendance_id: form.est_attendance_id ? parseInt(form.est_attendance_id) : null,
                image_url: form.image_url.trim() || null,
                festival_id: form.festival_id ? parseInt(form.festival_id) : null,
                promoter_ids: selectedPromoterIds,
                artists: lineup.map(a => ({
                    artist_id: parseInt(a.artist_id),
                    role: a.role,
                    lineup_position: a.lineup_position
                }))
            };
            const res = await axios.post(`${config.API_URL}/events`, payload);
            const newSlug = res.data.event ? res.data.event.slug : res.data.id;
            navigate(`/events/${newSlug}`);
        } catch (err) {
            setError('Failed to save event');
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading...</div>;

    return (
        <>
        <div style={styles.container}>
            <Link to="/" style={styles.backLink}>← Back to Home</Link>
            <h1 style={styles.title}>Add Event</h1>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.form}>
                <div style={styles.field}>
                    <label style={styles.label}>Event Date *</label>
                    <input type="date" name="event_date" value={form.event_date} onChange={handleFormChange} style={styles.input} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Venue *</label>
                    <SearchableSelect
                        options={venues}
                        value={form.venue_id}
                        onChange={val => {
                            setForm(prev => ({ ...prev, venue_id: val, stage_id: '' }));
                            setStages([]);
                            if (val) {
                                axios.get(`${config.API_URL}/venues/${val}`)
                                    .then(res => {
                                        const venueStages = res.data.stages || [];
                                        setStages(venueStages);
                                        const defaultStage = venueStages.find(s => s.is_default);
                                        if (defaultStage) setForm(prev => ({ ...prev, stage_id: String(defaultStage.id) }));
                                    })
                                    .catch(() => {});
                            }
                        }}
                        placeholder="Search venues..."
                        renderLabel={v => `${v.name} — ${v.city}${v.state ? `, ${v.state}` : ''}`}
                        onCreateItem={handleOpenCreateVenue}
                        createLabel="Create new venue"
                    />
                </div>

                {stages.length > 0 && (
                    <div style={styles.field}>
                        <label style={styles.label}>Stage</label>
                        <select name="stage_id" value={form.stage_id} onChange={handleFormChange} style={styles.input}>
                            <option value="">Select stage...</option>
                            {stages.map(s => (
                                <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' (Default)' : ''}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div style={styles.field}>
                    <label style={styles.label}>Event Title</label>
                    <input type="text" name="title" value={form.title} onChange={handleFormChange} placeholder="e.g. Summer Tour 2026, Festival Name..." style={styles.input} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Promoter(s)</label>
                    <PromoterPicker
                        promoters={promoters}
                        selectedIds={selectedPromoterIds}
                        onChange={setSelectedPromoterIds}
                        onCreatePromoter={handleOpenCreatePromoter}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Festival</label>
                    <SearchableSelect
                        options={festivals}
                        value={form.festival_id}
                        onChange={val => setForm(prev => ({ ...prev, festival_id: val }))}
                        placeholder="Search festivals..."
                        renderLabel={f => f.name}
                        onCreateItem={handleOpenCreateFestival}
                        createLabel="Create new festival"
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Estimated Attendance</label>
                    <select name="est_attendance_id" value={form.est_attendance_id} onChange={handleFormChange} style={styles.input}>
                        <option value="">Select attendance...</option>
                        {picklists.attendance_estimates.map(a => (
                            <option key={a.id} value={a.id}>{a.value}</option>
                        ))}
                    </select>
                </div>

                <div style={styles.lineupSection}>
                    <div style={styles.lineupHeader}>
                        <label style={styles.label}>Lineup *</label>
                        <button onClick={addArtist} style={styles.addArtistButton}>+ Add Artist</button>
                    </div>
                    {lineup.map((item, index) => (
                        <div key={index} style={styles.lineupRow}>
                            <div style={styles.lineupPosition}>#{item.lineup_position}</div>
                            <div style={{ flex: 2 }}>
                                <SearchableSelect
                                    options={artists}
                                    value={item.artist_id}
                                    onChange={val => handleLineupChange(index, 'artist_id', val)}
                                    placeholder="Search artists..."
                                    renderLabel={a => `${a.name}${a.city ? ` — ${a.city}` : ''}`}
                                    onCreateItem={name => handleOpenCreateArtist(index, name)}
                                    createLabel="Create new artist"
                                />
                            </div>
                            <select
                                value={item.role}
                                onChange={e => handleLineupChange(index, 'role', e.target.value)}
                                style={styles.roleSelect}
                            >
                                <option value="support">Support</option>
                                <option value="headliner">Headliner</option>
                            </select>
                            {lineup.length > 1 && (
                                <button onClick={() => removeArtist(index)} style={styles.removeButton}>✕</button>
                            )}
                        </div>
                    ))}
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Event Image URL</label>
                    <input type="text" name="image_url" value={form.image_url} onChange={handleFormChange} placeholder="https://... (poster or flyer image)" style={styles.input} />
                    {form.image_url && (
                        <img src={form.image_url} alt="Event poster preview" style={styles.imagePreview} onError={e => { e.target.style.display = 'none'; }} />
                    )}
                </div>

                <button onClick={handleSubmit} disabled={saving} style={styles.submitButton}>
                    {saving ? 'Saving...' : 'Save Event'}
                </button>
            </div>
        </div>

        {showCreateArtist && (
            <QuickCreateArtistModal
                picklists={picklists}
                initialName={createArtistInitialName}
                onCreated={handleArtistCreated}
                onClose={() => setShowCreateArtist(false)}
            />
        )}
        {showCreateFestival && (
            <QuickCreateFestivalModal
                initialName={createFestivalInitialName}
                onCreated={handleFestivalCreated}
                onClose={() => setShowCreateFestival(false)}
            />
        )}
        {showCreateVenue && (
            <QuickCreateVenueModal
                picklists={picklists}
                initialName={createVenueInitialName}
                onCreated={handleVenueCreated}
                onClose={() => setShowCreateVenue(false)}
            />
        )}
        {showCreatePromoter && (
            <QuickCreatePromoterModal
                picklists={picklists}
                initialName={createPromoterInitialName}
                onCreated={handlePromoterCreated}
                onClose={() => setShowCreatePromoter(false)}
            />
        )}
        </>
    );
}

const styles = {
    container: { maxWidth: '700px', margin: '0 auto' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '32px' },
    form: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '32px' },
    field: { marginBottom: '24px' },
    label: { display: 'block', fontSize: '14px', color: '#aaa', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    imagePreview: { marginTop: '12px', maxWidth: '160px', maxHeight: '200px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #333', display: 'block' },
    lineupSection: { marginBottom: '24px' },
    lineupHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
    addArtistButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '6px 12px', fontSize: '13px', cursor: 'pointer' },
    lineupRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' },
    lineupPosition: { color: '#aaa', fontSize: '14px', minWidth: '24px' },
    roleSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '8px 12px', fontSize: '14px', color: '#e0e0e0', flex: 1 },
    removeButton: { backgroundColor: 'transparent', color: '#cf6679', border: '1px solid #cf6679', borderRadius: '4px', padding: '6px 10px', fontSize: '13px', cursor: 'pointer' },
    submitButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '12px 24px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', width: '100%', marginTop: '8px' },
    error: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '12px 16px', color: '#cf6679', marginBottom: '24px', fontSize: '14px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
};

export default AddEvent;
